import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const base =
  'inline-flex items-center justify-center gap-2 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer';

const variants = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg',
  gradient:
    'bg-linear-to-r from-primary to-orange-400 text-white hover:from-primary/90 hover:to-orange-500 rounded-lg shadow-lg hover:shadow-xl',
  secondary: 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20 rounded-lg',
  ghost: 'text-muted-foreground hover:text-foreground hover:bg-primary/10 rounded-lg',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg',
  outline: 'border border-primary/30 bg-transparent text-foreground hover:bg-primary/10 rounded-lg',
} as const;

const sizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
  icon: 'p-2',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    return <button ref={ref} className={cn(base, variants[variant], sizes[size], className)} {...props} />;
  },
);
Button.displayName = 'Button';
